<template>
  <div class="field-wrapper">
    <PeriodSelector
      v-model="period"
      :units="units"
      title="automatedScenarios.fields.duration"
    />
    <FieldErrors
      :key="componentFieldErrorsKey"
      :errors="formValidation"
      field="time_unit_select"
    />
  </div>
</template>

<script lang="ts">
import { useI18n } from 'vue-i18n';
import { PeriodSelector } from '@/components/flows/operators/shared';
import {
  defineComponent,
  PropType,
  ref,
  SetupContext,
  watch,
} from 'vue';
import { asInt } from '@/helpers';
import { convertTimeUnitToSeconds } from '@/helpers/helpers';
import type { Period } from '@/types/flow-types';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import { maxBoxWaitDelayForOnSiteAutomation } from '@/composables/workflows/constants';
import type { WaitData } from './index';
import { createDefaultData } from './index';

export default defineComponent({
  name: 'ActionWaitForm',

  components: {
    FieldErrors,
    PeriodSelector,
  },

  props: {
    modelValue: {
      type: Object as PropType<WaitData>,
      required: true,
      default() {
        return createDefaultData();
      },
    },

    formValidation: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
  },

  emits: ['update:modelValue'],

  setup(props: { modelValue: WaitData; formValidation: any }, context: SetupContext) {
    const { t } = useI18n();
    const componentFieldErrorsKey = ref(0);

    watch(() => props.formValidation, () => {
      componentFieldErrorsKey.value += 1;
    });

    const units = [
      { value: 'SECOND', label: t('automatedScenarios.fields.durations.second(s)') },
      { value: 'MINUTE', label: t('automatedScenarios.fields.durations.minute(s)') },
      { value: 'HOUR', label: t('automatedScenarios.fields.durations.hour(s)') },
      { value: 'DAY', label: t('automatedScenarios.fields.durations.day(s)') },
      { value: 'MONTH', label: t('automatedScenarios.fields.durations.month(s)') },
    ];

    const period = ref<Period>({
      unit: props.modelValue.time_unit_select.unit,
      value: asInt(props.modelValue.time_unit_select.value),
    });

    const update = () => {
      const currentDelayInSeconds = convertTimeUnitToSeconds({
        value: period.value.value,
        unit: period.value.unit,
      });

      const updateData: WaitData = {
        time_unit_select: {
          value: period.value.value,
          unit: period.value.unit,
        },
      };

      // Only add event and webhook properties if delay is within limits
      if (currentDelayInSeconds <= maxBoxWaitDelayForOnSiteAutomation) {
        updateData.event = 'wait';
        updateData.webhook = props.modelValue.webhook || 0;
      }

      context.emit('update:modelValue', createDefaultData(updateData));
    };

    watch([period], update);

    return {
      componentFieldErrorsKey,
      units,
      period,
    };
  },
});
</script>
